import type { ComponentProps, FC } from "react";
import { useCallback } from "react";
import type { TickProps } from "@ptsd/uikit";
import {
  ChartContainer,
  ChartTooltip,
  getChartLines,
  Legend,
  YAxisTick,
} from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Circle, Text, View, XStack, YStack } from "tamagui";

import { api } from "~/utils/api";
import { dateToDDMM, dateToReadable } from "~/utils/date-to-readable";
import { normalizeConfig } from "~/utils/normalize";
import {
  GeneralFilter,
  GeneralFilterProvider,
  resolveFilter,
  useGeneralFilter,
} from "~/components/blocks/filters";
import { OverviewCard, OverviewEmptyCard } from "./overview-common";

export const OverviewInvolvement: FC = () => {
  const { t } = useTranslation("overview");

  return (
    <OverviewCard title={t("involvement.title")}>
      <GeneralFilterProvider>
        <YStack gap={"$8"} width="100%">
          <GeneralFilter
            withGroupBy
            shadow="none"
            variant="borderless"
            padding={0}
          />
          <InvolvementDynamicsChart />
        </YStack>
      </GeneralFilterProvider>
    </OverviewCard>
  );
};

type GroupBy = "week" | "month";

export const InvolvementDynamicsChart = (
  props: ComponentProps<typeof View>,
) => {
  const { t } = useTranslation("overview");

  const { filter } = useGeneralFilter();
  const { groupBy, ...rest } = filter;
  const [{ chartData, config }] =
    api.dashboard.getPatientActionDynamic.useSuspenseQuery(
      {
        groupBy: (groupBy as GroupBy) ?? "week",
        filter: resolveFilter(rest),
      },
      {
        select: ({ chartData, ...rest }) => ({
          config: normalizeConfig(rest),
          chartData,
        }),
      },
    );

  const labelDateFormatter = useCallback(
    (value: string) => dateToReadable(value, true),
    [],
  );
  const tickFormatter = useCallback(
    (v: string | number) => dateToDDMM(v.toString()) ?? "",
    [],
  );

  if (!chartData || chartData.length === 0) {
    return <OverviewEmptyCard text={t("balance.empty")} visible={true} />;
  }

  return (
    <View position="relative" gap={"$5"} {...props}>
      <ChartContainer
        data-id={"involvement-dynamics-chart-container"}
        height={362}
        config={config}
      >
        <LineChart
          data-id={"involvement-dynamics-chart"}
          accessibilityLayer
          data={chartData}
          margin={{
            left: -15,
            right: 40,
            top: 12,
            bottom: 20,
          }}
        >
          <CartesianGrid
            data-id={"involvement-dynamics-chart-grid"}
            strokeDasharray={"3 3"}
            stroke="#CBCBCC"
            vertical={false}
          />
          <XAxis
            data-id={"involvement-dynamics-chart-x-axis"}
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={24}
            tick={{
              fill: "#636668",
              fontFamily: "Fixel Text",
              fontSize: 14,
              fontWeight: 400,
            }}
            tickFormatter={tickFormatter}
          />
          <YAxis
            data-id={"involvement-dynamics-chart-y-axis"}
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tickMargin={16}
            tick={(props: TickProps) => <YAxisTick {...props} />}
          />
          <Tooltip
            data-id={"involvement-dynamics-chart-tooltip"}
            cursor={{
              stroke: "#CBCBCC",
              strokeWidth: 1,
              strokeDasharray: "3 3",
            }}
            content={
              <ChartTooltip.Content
                data-id={"involvement-dynamics-chart-tooltip-content"}
              >
                {(payload, _, date) => {
                  return (
                    <ChartTooltip.Container
                      data-id={"involvement-dynamics-chart-tooltip-container"}
                    >
                      <ChartTooltip.Title
                        data-id={"involvement-dynamics-chart-tooltip-title"}
                      >
                        {labelDateFormatter(date ?? "")}
                      </ChartTooltip.Title>
                      <View
                        gap={"$0.5"}
                        data-id={"involvement-dynamics-chart-tooltip-body"}
                      >
                        {payload?.map(({ color, value, config }, i) => (
                          <XStack
                            data-id={`involvement-dynamics-chart-tooltip-item-${i}`}
                            gap={"$0.5"}
                            alignItems="center"
                            key={i}
                          >
                            <Circle
                              backgroundColor={"white"}
                              borderWidth={"$0.3"}
                              borderColor={color}
                              size={"$2"}
                            />

                            <Text
                              data-id={`involvement-dynamics-chart-tooltip-text-${i}`}
                              fontFamily={"$body"}
                              fontSize={"$1"}
                              color={"black"}
                            >
                              {config?.label} {value}
                            </Text>
                          </XStack>
                        ))}
                      </View>
                    </ChartTooltip.Container>
                  );
                }}
              </ChartTooltip.Content>
            }
          />
          {getChartLines(config).map(({ dataKey, color }, i) => (
            <Line
              data-id={`involvement-dynamics-chart-line`}
              data-key={dataKey}
              key={i}
              dataKey={dataKey}
              type="linear"
              stroke={color}
              strokeWidth={2.5}
              dot={{
                r: 8,
                strokeWidth: 2.5,
                stroke: color,
                fill: "white",
              }}
              activeDot={{
                r: 3,
                strokeWidth: 2.5,
                stroke: color,
                fill: color,
              }}
            />
          ))}
        </LineChart>
      </ChartContainer>

      <Legend config={config} />
    </View>
  );
};
